"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getNodeViewWithoutFilterInfo = exports.getCloseSyncViewIds = exports.getViewById = exports.getViewInNode = exports.getViewIdByNodeId = exports.getActiveViewId = exports.getGanttSettingPanelVisible = exports.getGanttViewStatus = exports.getGanttRowHeightFromLevel = exports.getWidgetPanelStatus = exports.getWidgetPanels = exports.getDatasheetParentId = exports.allowShowCommentPane = exports.getLinkId = exports.getToolbarMenuCardState = exports.getNodeId = exports.getViewsList = exports.getField = exports.getRecord = exports.getNodeDesc = exports.getEditingCell = exports.getKanbanGroupCollapse = exports.getGroupingCollapseIds = exports.gridViewActiveFieldState = exports.getGridViewHoverFieldId = exports.getGridViewDragState = exports.getSearchResultCursorIndex = exports.getActiveRecordId = exports.getActiveCell = exports.getIsSearching = exports.getActiveRowInfo = exports.getSearchKeyword = exports.getFormSheetAccessibleByFieldId = exports.getFieldRoleByFieldId = exports.getFieldPermissionMap = exports.getFieldPermissionMapBase = exports.getHighlightFieldId = exports.getActiveDatasheetId = exports.getSnapshot = exports.getDatasheetClient = exports.getDatasheetConnected = exports.getDatasheetErrorCode = exports.getDatasheetLoading = exports.getDatasheetNetworking = exports.getDatasheetRole = exports.getRecordHistoryStatus = exports.getDatasheet = exports.getDatasheetPack = exports.getDatasheetPrimaryField = void 0;
const get_1 = __importDefault(require("lodash/get"));
const re_reselect_1 = __importDefault(require("re-reselect"));
const constants_1 = require("../../../../../shared/store/constants");
const interfaces_1 = require("../../../../../../exports/store/interfaces");
const resource_1 = require("../../../reducers/resource");
const mirror_1 = require("../mirror");
const defaultKeySelector = (state, datasheetId) => datasheetId || (0, exports.getNodeId)(state);
const getDatasheetPrimaryField = (snapshot) => {
    const firstView = snapshot.meta.views[0];
    const firstColumn = firstView.columns[0];
    const fieldMap = snapshot.meta.fieldMap;
    return fieldMap[firstColumn.fieldId];
};
exports.getDatasheetPrimaryField = getDatasheetPrimaryField;
/**
 * get a datasheet pack,
 * a datasheet pack contains the state information of the datasheet (loading, etc.),
 * the basic snapshot data, and the calculated information after calculation
 * @param state
 * @param id
 * @returns
 */
const getDatasheetPack = (state, id) => {
    var _a;
    const datasheetId = id || ((_a = state.pageParams) === null || _a === void 0 ? void 0 : _a.datasheetId);
    if (!datasheetId) {
        return;
    }
    // if no specify datasheet id, and have snapshot preview data, use preview data for render
    if (!id && state.datasheetMap[constants_1.PREVIEW_DATASHEET_ID]) {
        return state.datasheetMap[constants_1.PREVIEW_DATASHEET_ID];
    }
    return state.datasheetMap[datasheetId];
};
exports.getDatasheetPack = getDatasheetPack;
/**
 * get the snapshot of datasheet
 * @param state
 * @param id
 * @returns
 */
const getDatasheet = (state, id) => {
    const datasheetPack = (0, exports.getDatasheetPack)(state, id);
    return datasheetPack && datasheetPack.datasheet;
};
exports.getDatasheet = getDatasheet;
const getRecordHistoryStatus = (state, id) => {
    const datasheetId = id || state.pageParams.datasheetId;
    if (!datasheetId) {
        return false;
    }
    return (0, get_1.default)(state, `datasheetMap.${datasheetId}.datasheet.extra.showRecordHistory`, false);
};
exports.getRecordHistoryStatus = getRecordHistoryStatus;
const getDatasheetRole = (state, id) => {
    const datasheetId = id || state.pageParams.datasheetId;
    if (!datasheetId) {
        return;
    }
    return (0, get_1.default)(state, `datasheetMap.${datasheetId}.datasheet.role`);
};
exports.getDatasheetRole = getDatasheetRole;
const getDatasheetNetworking = (state, id) => {
    const datasheetPack = (0, exports.getDatasheetPack)(state, id);
    if (!datasheetPack) {
        return;
    }
    return {
        loading: datasheetPack.loading,
        connected: datasheetPack.connected,
        syncing: datasheetPack.syncing,
        errorCode: datasheetPack.errorCode,
    };
};
exports.getDatasheetNetworking = getDatasheetNetworking;
const getDatasheetLoading = (state, id) => {
    var _a;
    return (_a = (0, exports.getDatasheetNetworking)(state, id)) === null || _a === void 0 ? void 0 : _a.loading;
};
exports.getDatasheetLoading = getDatasheetLoading;
const getDatasheetErrorCode = (state, id) => {
    var _a;
    return (_a = (0, exports.getDatasheetNetworking)(state, id)) === null || _a === void 0 ? void 0 : _a.errorCode;
};
exports.getDatasheetErrorCode = getDatasheetErrorCode;
const getDatasheetConnected = (state, id) => {
    var _a;
    return (_a = (0, exports.getDatasheetNetworking)(state, id)) === null || _a === void 0 ? void 0 : _a.connected;
};
exports.getDatasheetConnected = getDatasheetConnected;
const getDatasheetClient = (state, id) => {
    const datasheetPack = (0, exports.getDatasheetPack)(state, id);
    return datasheetPack === null || datasheetPack === void 0 ? void 0 : datasheetPack.client;
};
exports.getDatasheetClient = getDatasheetClient;
const getSnapshot = (state, id) => {
    const datasheet = (0, exports.getDatasheet)(state, id);
    return datasheet === null || datasheet === void 0 ? void 0 : datasheet.snapshot;
};
exports.getSnapshot = getSnapshot;
const getActiveDatasheetId = (state) => {
    return state.pageParams.datasheetId;
};
exports.getActiveDatasheetId = getActiveDatasheetId;
const getHighlightFieldId = (state, id) => {
    var _a;
    const datasheetPack = (0, exports.getDatasheetPack)(state, id);
    return (_a = datasheetPack === null || datasheetPack === void 0 ? void 0 : datasheetPack.client) === null || _a === void 0 ? void 0 : _a.highlightFiledId;
};
exports.getHighlightFieldId = getHighlightFieldId;
// get current datasheet's column permission
const getFieldPermissionMapBase = (state, id) => {
    const datasheetPack = (0, exports.getDatasheetPack)(state, id);
    return datasheetPack === null || datasheetPack === void 0 ? void 0 : datasheetPack.fieldPermissionMap;
};
exports.getFieldPermissionMapBase = getFieldPermissionMapBase;
exports.getFieldPermissionMap = (0, re_reselect_1.default)([
    exports.getFieldPermissionMapBase,
    state => {
        var _a;
        const snapshot = (0, exports.getSnapshot)(state);
        return (0, exports.getViewById)(snapshot, ((_a = state.pageParams) === null || _a === void 0 ? void 0 : _a.viewId) || '');
    },
    state => { var _a; return (_a = state.pageParams) === null || _a === void 0 ? void 0 : _a.mirrorId; },
], (fieldPermissionMap, view, mirrorId) => {
    if (!mirrorId) {
        return fieldPermissionMap;
    }
    if (mirrorId && view && typeof view.displayHiddenColumnWithinMirror === 'boolean' && !view.displayHiddenColumnWithinMirror) {
        const _fieldPermissionMap = {};
        for (const v of view.columns) {
            if (!v.hidden)
                continue;
            _fieldPermissionMap[v.fieldId] = {
                role: interfaces_1.Role.None,
                setting: {
                    formSheetAccessible: true,
                },
                permission: {
                    editable: false,
                    readable: false
                },
                manageable: false
            };
        }
        return Object.assign(Object.assign({}, fieldPermissionMap), _fieldPermissionMap);
    }
    return fieldPermissionMap;
})(defaultKeySelector);
const getFieldRoleByFieldId = (fieldPermissionMap, fieldId) => {
    if (!fieldPermissionMap || !fieldPermissionMap[fieldId]) {
        return null;
    }
    const role = fieldPermissionMap[fieldId].role;
    return role || interfaces_1.Role.None;
};
exports.getFieldRoleByFieldId = getFieldRoleByFieldId;
const getFormSheetAccessibleByFieldId = (fieldPermissionMap, fieldId) => {
    if (!fieldPermissionMap || !fieldPermissionMap[fieldId]) {
        return true;
    }
    return fieldPermissionMap[fieldId].setting.formSheetAccessible;
};
exports.getFormSheetAccessibleByFieldId = getFormSheetAccessibleByFieldId;
const getSearchKeyword = (state, dstId) => {
    const dstClient = (0, exports.getDatasheetClient)(state, dstId);
    return dstClient === null || dstClient === void 0 ? void 0 : dstClient.searchKeyword;
};
exports.getSearchKeyword = getSearchKeyword;
const getActiveRowInfo = (state, dstId) => {
    const dstClient = (0, exports.getDatasheetClient)(state, dstId);
    return dstClient === null || dstClient === void 0 ? void 0 : dstClient.activeRowInfo;
};
exports.getActiveRowInfo = getActiveRowInfo;
const getIsSearching = (state, dstId) => {
    const dstClient = (0, exports.getDatasheetClient)(state, dstId);
    return Boolean(dstClient === null || dstClient === void 0 ? void 0 : dstClient.searchKeyword);
};
exports.getIsSearching = getIsSearching;
const getActiveCell = (state, datasheetId) => {
    const client = (0, exports.getDatasheetClient)(state, datasheetId);
    const activeCell = client && client.selection && client.selection.activeCell;
    return activeCell ? activeCell : null;
};
exports.getActiveCell = getActiveCell;
const getActiveRecordId = (state) => {
    const activeCell = (0, exports.getActiveCell)(state);
    return activeCell === null || activeCell === void 0 ? void 0 : activeCell.recordId;
};
exports.getActiveRecordId = getActiveRecordId;
const getSearchResultCursorIndex = (state, dstId) => {
    const dstClient = (0, exports.getDatasheetClient)(state, dstId);
    return dstClient === null || dstClient === void 0 ? void 0 : dstClient.searchResultCursorIndex;
};
exports.getSearchResultCursorIndex = getSearchResultCursorIndex;
const getGridViewDragState = (state, id) => {
    const client = (0, exports.getDatasheetClient)(state, id);
    return client ? client.gridViewDragState : resource_1.gridViewDragStateDefault;
};
exports.getGridViewDragState = getGridViewDragState;
const getGridViewHoverFieldId = (state, id) => {
    const client = (0, exports.getDatasheetClient)(state, id);
    return client ? client.gridViewHoverFieldId : null;
};
exports.getGridViewHoverFieldId = getGridViewHoverFieldId;
const gridViewActiveFieldState = (state, id) => {
    const client = (0, exports.getDatasheetClient)(state, id);
    return client ? client.gridViewActiveFieldState : resource_1.gridViewActiveFieldStateDefault;
};
exports.gridViewActiveFieldState = gridViewActiveFieldState;
const getGroupingCollapseIds = (state, datasheetId) => {
    const client = (0, exports.getDatasheetClient)(state, datasheetId);
    return client && client.groupingCollapseIds;
};
exports.getGroupingCollapseIds = getGroupingCollapseIds;
const getKanbanGroupCollapse = (state) => {
    const client = (0, exports.getDatasheetClient)(state);
    if (!client || !client.kanbanGroupCollapse) {
        return [];
    }
    return client.kanbanGroupCollapse;
};
exports.getKanbanGroupCollapse = getKanbanGroupCollapse;
const getEditingCell = (state) => {
    const client = (0, exports.getDatasheetClient)(state);
    if (!client || !client.isEditingCell) {
        return null;
    }
    return client.isEditingCell;
};
exports.getEditingCell = getEditingCell;
const getNodeDesc = (state, dsId) => {
    const datasheet = (0, exports.getDatasheet)(state, dsId);
    if (!datasheet || !datasheet.description) {
        return null;
    }
    return JSON.parse(datasheet.description);
};
exports.getNodeDesc = getNodeDesc;
const getRecord = (state, recordId, datasheetId) => {
    const snapshot = (0, exports.getSnapshot)(state, datasheetId);
    return snapshot.recordMap[recordId];
};
exports.getRecord = getRecord;
const getField = (state, fieldId, datasheetId) => {
    const snapshot = (0, exports.getSnapshot)(state, datasheetId);
    if (!snapshot) {
        return constants_1.NotSupportFieldInstance;
    }
    return snapshot.meta.fieldMap[fieldId] || constants_1.NotSupportFieldInstance;
};
exports.getField = getField;
const getViewsList = (state, dsId) => {
    const snapshot = (0, exports.getSnapshot)(state, dsId);
    return snapshot.meta.views;
};
exports.getViewsList = getViewsList;
const getNodeId = (state) => {
    const { datasheetId, folderId, formId, dashboardId, mirrorId } = state.pageParams;
    // mirror is special,  url will contain mirrorId and datasheetId at the same time, so mirrorId need to be judged first
    if (mirrorId) {
        return mirrorId;
    }
    if (datasheetId) {
        return datasheetId;
    }
    if (folderId) {
        return folderId;
    }
    if (formId) {
        return formId;
    }
    if (dashboardId) {
        return dashboardId;
    }
    return '';
};
exports.getNodeId = getNodeId;
const getToolbarMenuCardState = (state) => {
    return state.toolbar.menuCardState;
};
exports.getToolbarMenuCardState = getToolbarMenuCardState;
const getLinkId = (state) => {
    const pageParams = state.pageParams;
    return pageParams.templateId || pageParams.shareId || pageParams.embedId;
};
exports.getLinkId = getLinkId;
const allowShowCommentPane = (state) => {
    const spaceId = state.space.activeId;
    const linkId = (0, exports.getLinkId)(state);
    const embedId = state.pageParams.embedId;
    return Boolean(spaceId && !linkId) || Boolean(spaceId && embedId);
};
exports.allowShowCommentPane = allowShowCommentPane;
const getDatasheetParentId = (state, id) => {
    var _a;
    const tree = state.catalogTree.treeNodesMap;
    const datasheet = (0, exports.getDatasheet)(state, id);
    if (!datasheet) {
        return;
    }
    return ((_a = tree[datasheet.id]) === null || _a === void 0 ? void 0 : _a.parentId) || datasheet.parentId;
};
exports.getDatasheetParentId = getDatasheetParentId;
const getWidgetPanels = (state, datasheetId) => {
    const snapshot = (0, exports.getSnapshot)(state, datasheetId);
    return snapshot && snapshot.meta.widgetPanels;
};
exports.getWidgetPanels = getWidgetPanels;
const getWidgetPanelStatus = (state, datasheetId) => {
    const client = (0, exports.getDatasheetClient)(state, datasheetId);
    return client === null || client === void 0 ? void 0 : client.widgetPanelStatus;
};
exports.getWidgetPanelStatus = getWidgetPanelStatus;
const getGanttRowHeightFromLevel = (level) => {
    return level == null ? constants_1.GanttRowHeight.Short : constants_1.GanttRowHeight[constants_1.RowHeightLevel[level]];
};
exports.getGanttRowHeightFromLevel = getGanttRowHeightFromLevel;
const getGanttViewStatus = (state, datasheetId) => {
    const client = (0, exports.getDatasheetClient)(state, datasheetId);
    return client === null || client === void 0 ? void 0 : client.ganttViewStatus;
};
exports.getGanttViewStatus = getGanttViewStatus;
const getGanttSettingPanelVisible = (state, datasheetId) => {
    const ganttViewStatus = (0, exports.getGanttViewStatus)(state, datasheetId);
    return ganttViewStatus.settingPanelVisible;
};
exports.getGanttSettingPanelVisible = getGanttSettingPanelVisible;
const getActiveViewId = (state, id) => {
    var _a;
    const datasheet = (0, exports.getDatasheet)(state, id);
    const views = datasheet === null || datasheet === void 0 ? void 0 : datasheet.snapshot.meta.views;
    const pageViewId = state.pageParams.viewId;
    if (!views) {
        return pageViewId;
    }
    if (!views.find(item => item.id === pageViewId)) {
        return (_a = views[0]) === null || _a === void 0 ? void 0 : _a.id;
    }
    return pageViewId;
};
exports.getActiveViewId = getActiveViewId;
const getViewIdByNodeId = (state, datasheetId, viewId, mirror) => {
    const _mirror = mirror || (0, mirror_1.getMirror)(state);
    const _viewId = viewId !== null && viewId !== void 0 ? viewId : state.pageParams.viewId;
    const isMirrorView = _mirror && _mirror.sourceInfo.datasheetId === datasheetId && _mirror.sourceInfo.viewId === _viewId;
    return isMirrorView ? _mirror === null || _mirror === void 0 ? void 0 : _mirror.id : _viewId;
};
exports.getViewIdByNodeId = getViewIdByNodeId;
// Get current node view (contains mirror & datasheet).
const getViewInNode = (state, datasheetId, viewId) => {
    const _viewId = viewId || (0, exports.getActiveViewId)(state, datasheetId);
    const nodeViewId = (0, exports.getViewIdByNodeId)(state, datasheetId, _viewId);
    const snapshot = (0, exports.getSnapshot)(state, datasheetId);
    if (!snapshot || !_viewId) {
        return;
    }
    const mirror = (0, mirror_1.getMirror)(state, nodeViewId);
    if (nodeViewId && mirror) {
        const temporaryView = (0, exports.getNodeViewWithoutFilterInfo)(snapshot, _viewId, mirror);
        return Object.assign(Object.assign({}, temporaryView), { id: nodeViewId });
    }
    return (0, exports.getViewById)(snapshot, _viewId);
};
exports.getViewInNode = getViewInNode;
const getViewById = (snapshot, viewId) => {
    return snapshot === null || snapshot === void 0 ? void 0 : snapshot.meta.views.find(view => view.id === viewId);
};
exports.getViewById = getViewById;
const getCloseSyncViewIds = (state, dsId) => {
    const client = (0, exports.getDatasheetClient)(state, dsId);
    return client === null || client === void 0 ? void 0 : client.closeSyncViewIds;
};
exports.getCloseSyncViewIds = getCloseSyncViewIds;
/**
 * Get the node view configuration, mirroring does not include filtering,
 * it will be done inside the filtering logic.
 * @param viewId
 * @param mirror
 */
const getNodeViewWithoutFilterInfo = (snapshot, viewId, mirror) => {
    const temporaryView = mirror === null || mirror === void 0 ? void 0 : mirror.temporaryView;
    if (!snapshot) {
        return;
    }
    const originView = (0, exports.getViewById)(snapshot, viewId);
    if (!temporaryView || (mirror === null || mirror === void 0 ? void 0 : mirror.sourceInfo.datasheetId) !== snapshot.datasheetId) {
        return originView;
    }
    // If any view configuration is modified in the mirror,
    // the view configuration operation of the original table will not affect the mirror anymore,
    // so the cached data of the mirror is taken directly here.
    return Object.assign(Object.assign({ id: originView.id, type: originView.type, rows: originView.rows }, temporaryView), { filterInfo: originView === null || originView === void 0 ? void 0 : originView.filterInfo });
};
exports.getNodeViewWithoutFilterInfo = getNodeViewWithoutFilterInfo;
//# sourceMappingURL=base.js.map